/**
 * Deployment detail — progress, controls, target table.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Pause, Play, SkipForward, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { useGatewayDeployment } from '@/hooks/useGatewayDeployment';
import { useGatewayDeployments } from '@/hooks/useGatewayDeployments';
import type { GatewayDeployment, GatewayDeploymentTarget, DeploymentStatus, DeploymentTargetStatus } from '@/types/gateway';
import { toast } from 'sonner';

const STATUS_BADGE: Record<DeploymentStatus, string> = {
  pending: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  in_progress: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20',
  paused: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20',
  completed: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20',
  cancelled: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  failed: 'bg-wt-red-primary/10 text-wt-red-primary border-wt-red-primary/20',
};

const TARGET_STATUS: Record<DeploymentTargetStatus, string> = {
  pending: 'text-gray-400',
  queued: 'text-blue-400',
  downloading: 'text-blue-400',
  installing: 'text-wt-amber-primary',
  success: 'text-wt-green-primary',
  failed: 'text-wt-red-primary',
  skipped: 'text-gray-500',
  rolled_back: 'text-wt-red-primary',
};

export interface DeploymentDetailViewProps {
  deploymentId: string;
  onBack: () => void;
}

export function DeploymentDetailView({ deploymentId, onBack }: DeploymentDetailViewProps) {
  const { deployment, targets, isLoading, error, refetch } = useGatewayDeployment(deploymentId);
  const {
    advanceDeployment,
    pauseDeployment,
    resumeDeployment,
    cancelDeployment,
  } = useGatewayDeployments();

  const handleAdvance = async () => {
    try {
      await advanceDeployment(deploymentId);
      toast.success('Advanced to next stage');
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to advance');
    }
  };

  const handlePause = async () => {
    try {
      await pauseDeployment(deploymentId);
      toast.success('Deployment paused');
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to pause');
    }
  };

  const handleResume = async () => {
    try {
      await resumeDeployment(deploymentId);
      toast.success('Deployment resumed');
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to resume');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelDeployment(deploymentId);
      toast.success('Deployment cancelled');
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to cancel');
    }
  };

  if (isLoading || !deployment) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-wt-text-secondary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <p className="text-sm text-wt-red-primary">Failed to load deployment</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={onBack}>Back</Button>
      </div>
    );
  }

  const total = deployment.total_gateways || 0;
  const updated = deployment.updated_gateways || 0;
  const failed = deployment.failed_gateways || 0;
  const cfg = deployment.staged_config;
  const currentStageIndex = cfg?.current_stage_index ?? 0;
  const stages = cfg?.stages ?? [10, 25, 50, 100];
  const canAdvance =
    deployment.status === 'in_progress' &&
    deployment.strategy === 'staged' &&
    currentStageIndex + 1 < stages.length;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
      </div>

      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base text-wt-text-primary">
              {deployment.name || `Deployment ${deployment.id.slice(0, 8)}`}
            </CardTitle>
            <Badge variant="outline" className={STATUS_BADGE[deployment.status]}>
              {deployment.status.replace('_', ' ')}
            </Badge>
          </div>
          <p className="text-sm text-wt-text-secondary">
            Firmware {deployment.firmware_version} · {deployment.strategy} · Created{' '}
            {format(new Date(deployment.created_at), 'MMM d, yyyy HH:mm')}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-2 rounded-full bg-wt-bg-tertiary overflow-hidden flex">
            <div
              className="h-full bg-wt-green-primary"
              style={{ width: total ? `${(updated / total) * 100}%` : 0 }}
            />
            <div
              className="h-full bg-wt-red-primary"
              style={{ width: total ? `${(failed / total) * 100}%` : 0 }}
            />
          </div>
          <p className="text-sm font-mono-numeric text-wt-text-secondary">
            {updated} updated · {failed} failed · {total - updated - failed} remaining
          </p>

          {deployment.strategy === 'staged' && (
            <p className="text-xs text-wt-text-secondary">
              Stage {currentStageIndex + 1} of {stages.length}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {deployment.status === 'in_progress' && (
              <>
                <Button variant="outline" size="sm" onClick={handlePause}>
                  <Pause className="w-3.5 h-3.5 mr-1" />
                  Pause
                </Button>
                {canAdvance && (
                  <Button variant="outline" size="sm" onClick={handleAdvance}>
                    <SkipForward className="w-3.5 h-3.5 mr-1" />
                    Advance to next stage
                  </Button>
                )}
              </>
            )}
            {deployment.status === 'paused' && (
              <Button variant="outline" size="sm" onClick={handleResume}>
                <Play className="w-3.5 h-3.5 mr-1" />
                Resume
              </Button>
            )}
            {(deployment.status === 'in_progress' || deployment.status === 'paused' || deployment.status === 'pending') && (
              <Button variant="outline" size="sm" onClick={handleCancel} className="text-wt-red-primary">
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-wt-text-primary">Targets</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-wt-text-secondary text-left">
                  <th className="px-4 py-2 font-medium">Gateway</th>
                  <th className="px-4 py-2 font-medium">Property</th>
                  <th className="px-4 py-2 font-medium">Stage</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Previous</th>
                  <th className="px-4 py-2 font-medium">Completed</th>
                </tr>
              </thead>
              <tbody>
                {targets
                  .sort((a, b) => {
                    const order = ['failed', 'rolled_back', 'success', 'downloading', 'installing', 'queued', 'pending', 'skipped'];
                    return order.indexOf(a.status) - order.indexOf(b.status);
                  })
                  .map((t) => (
                    <tr key={t.id} className="border-b border-border/50">
                      <td className="px-4 py-2 font-mono-numeric text-wt-text-primary">
                        {t.gateway_mac_address ?? t.gateway_id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-2 text-wt-text-secondary">{t.property_name ?? '—'}</td>
                      <td className="px-4 py-2 font-mono-numeric">{t.stage}</td>
                      <td className={`px-4 py-2 ${TARGET_STATUS[t.status]}`}>{t.status}</td>
                      <td className="px-4 py-2 font-mono-numeric text-wt-text-secondary">{t.previous_version ?? '—'}</td>
                      <td className="px-4 py-2 text-wt-text-secondary">
                        {t.completed_at ? format(new Date(t.completed_at), 'MMM d HH:mm') : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {targets.length === 0 && (
            <div className="py-6 text-center text-wt-text-secondary text-sm">No targets</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
